import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { DEPT_LABEL, canManageContent } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Folder, FolderPlus, ChevronRight, Home, Upload, Search, FileText, FileImage, FileVideo, FileArchive,
  Download, Trash2, MoreHorizontal, LayoutGrid, List, Star, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

function iconForFile(ct = "", name = "") {
  const n = name.toLowerCase();
  if (ct.startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(n)) return FileImage;
  if (ct.startsWith("video/") || /\.(mp4|mov|webm)$/i.test(n)) return FileVideo;
  if (/\.(zip|rar|7z)$/i.test(n)) return FileArchive;
  return FileText;
}

function humanSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function DepartmentPage() {
  const { dept } = useParams();
  const { user } = useAuth();
  const [folder, setFolder] = useState(null); // null = root
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [subfolders, setSubfolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const canManage = canManageContent(user);

  const load = useCallback(async () => {
    try {
      const parent_id = folder?.id || null;
      const folderParams = { department: dept };
      const docParams = { department: dept };
      if (parent_id) {
        folderParams.parent_id = parent_id;
        docParams.folder_id = parent_id;
      }
      if (search) docParams.q = search;
      const [fRes, dRes] = await Promise.all([
        api.get("/folders", { params: folderParams }),
        api.get("/documents", { params: docParams }),
      ]);
      setSubfolders(fRes.data || []);
      setDocuments(dRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load folder");
    }
  }, [dept, folder, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // reset when department changes
    setFolder(null);
    setBreadcrumb([]);
  }, [dept]);

  const openFolder = async (f) => {
    setFolder(f);
    try {
      const { data } = await api.get(`/folders/${f.id}`);
      setBreadcrumb(data.breadcrumb || []);
    } catch (_) { setBreadcrumb([{ id: f.id, name: f.name }]); }
  };

  const goRoot = () => {
    setFolder(null);
    setBreadcrumb([]);
  };

  const goCrumb = async (crumb, idx) => {
    if (idx === breadcrumb.length - 1) return;
    const target = { id: crumb.id, name: crumb.name };
    setFolder(target);
    try {
      const { data } = await api.get(`/folders/${crumb.id}`);
      setBreadcrumb(data.breadcrumb || []);
    } catch (_) {}
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post("/folders", {
        name: newFolderName.trim(),
        parent_id: folder?.id || null,
        department: dept,
        project_id: null,
        sort_order: 0,
      });
      setNewFolderName("");
      setShowNewFolder(false);
      toast.success("Folder created");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create folder");
    }
  };

  const doUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("department", dept);
        if (folder?.id) fd.append("folder_id", folder.id);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok++;
      } catch (e) {
        fail++;
      }
    }
    setUploading(false);
    if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
    if (fail) toast.error(`${fail} upload${fail > 1 ? "s" : ""} failed`);
    load();
  };

  const deleteFolder = async (f) => {
    if (!window.confirm(`Delete folder "${f.name}" and all its contents? This cannot be undone.`)) return;
    try {
      await api.delete(`/folders/${f.id}`);
      toast.success("Folder deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteDoc = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || d.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const renameFolder = async (f) => {
    const name = window.prompt("Rename folder", f.name);
    if (!name || name === f.name) return;
    try {
      await api.patch(`/folders/${f.id}`, { name });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const renameDoc = async (d) => {
    const name = window.prompt("Rename file", d.name);
    if (!name || name === d.name) return;
    try {
      await api.patch(`/documents/${d.id}`, { name });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleImportant = async (d) => {
    try {
      await api.patch(`/documents/${d.id}`, { is_important: !d.is_important });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canManage) return;
    doUpload(e.dataTransfer.files);
  };

  const filteredDocs = useMemo(() => {
    if (!search) return documents;
    const q = search.toLowerCase();
    return documents.filter(
      (d) => d.name.toLowerCase().includes(q) || (d.description || "").toLowerCase().includes(q)
    );
  }, [documents, search]);

  return (
    <div className="p-6 lg:p-12" data-testid={`department-${dept}`}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-1">Department</div>
          <h1 className="font-heading font-light text-3xl lg:text-4xl tracking-tighter">
            {DEPT_LABEL[dept] || dept}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone" />
            <Input
              data-testid="dept-search"
              placeholder="Search files…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 w-56 bg-white"
            />
          </div>
          <div className="border border-line bg-white flex">
            <button onClick={() => setView("grid")} data-testid="view-grid" className={`p-2 ${view === "grid" ? "bg-ink text-white" : "text-stone"}`}><LayoutGrid size={14} /></button>
            <button onClick={() => setView("list")} data-testid="view-list" className={`p-2 ${view === "list" ? "bg-ink text-white" : "text-stone"}`}><List size={14} /></button>
          </div>
          {canManage && (
            <>
              <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="new-folder-btn" className="rounded-none border-line hover:border-ink">
                    <FolderPlus size={14} className="mr-2" /> New folder
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-heading font-light text-2xl">New folder</DialogTitle>
                    <DialogDescription>Create a folder inside <strong>{folder?.name || DEPT_LABEL[dept]}</strong>.</DialogDescription>
                  </DialogHeader>
                  <Input
                    autoFocus
                    data-testid="new-folder-input"
                    placeholder="Folder name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") createFolder(); }}
                  />
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setShowNewFolder(false)}>Cancel</Button>
                    <Button className="bg-ink text-white hover:bg-ink/90 rounded-none" onClick={createFolder} data-testid="new-folder-create">Create</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <label className="cursor-pointer border border-ink bg-ink text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-ink/90" data-testid="upload-btn">
                <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
                <input type="file" multiple hidden onChange={(e) => doUpload(e.target.files)} disabled={uploading} data-testid="upload-input" />
              </label>
            </>
          )}
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="flex items-center flex-wrap gap-1 text-sm text-stone mb-6" data-testid="breadcrumbs">
        <button onClick={goRoot} className="hover:text-ink inline-flex items-center gap-1">
          <Home size={13} /> {DEPT_LABEL[dept]}
        </button>
        {breadcrumb.map((c, idx) => (
          <React.Fragment key={c.id}>
            <ChevronRight size={13} />
            <button onClick={() => goCrumb(c, idx)} className={`hover:text-ink ${idx === breadcrumb.length - 1 ? "text-ink font-medium" : ""}`}>
              {c.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Content */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative min-h-[400px] border ${dragOver ? "border-ink border-dashed bg-sand/60" : "border-transparent"} transition-colors`}
      >
        {dragOver && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="font-heading text-2xl text-ink">Drop files to upload</div>
          </div>
        )}

        {subfolders.length === 0 && filteredDocs.length === 0 ? (
          <div className="border border-line bg-white p-14 text-center" data-testid="empty-folder">
            <Folder size={40} strokeWidth={1} className="mx-auto text-stone mb-4" />
            <div className="font-heading text-xl">This folder is empty</div>
            <div className="text-sm text-stone mt-2">
              {canManage ? "Create a subfolder or drag files here to upload." : "There's nothing here yet."}
            </div>
          </div>
        ) : view === "grid" ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {subfolders.map((f) => (
              <div key={f.id} className="group border border-line bg-white hover:border-ink transition-colors" data-testid={`folder-${f.id}`}>
                <button
                  onClick={() => openFolder(f)}
                  className="w-full aspect-square flex flex-col items-center justify-center p-4 text-left"
                >
                  <Folder size={40} strokeWidth={1.4} className="text-ink/70 group-hover:text-ink" />
                  <div className="mt-3 text-sm text-center truncate w-full font-medium">{f.name}</div>
                </button>
                {canManage && (
                  <div className="border-t border-line px-2 py-1 flex justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger className="text-stone hover:text-ink p-1"><MoreHorizontal size={14} /></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem onClick={() => renameFolder(f)}>Rename</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive" onClick={() => deleteFolder(f)}>Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            ))}

            {filteredDocs.map((d) => {
              const Icon = iconForFile(d.content_type, d.name);
              return (
                <div key={d.id} className="group border border-line bg-white hover:border-ink transition-colors flex flex-col" data-testid={`document-${d.id}`}>
                  <button
                    onClick={() => downloadDoc(d)}
                    className="flex-1 aspect-square flex flex-col items-center justify-center p-4"
                  >
                    <Icon size={36} strokeWidth={1.4} className="text-ink/70" />
                    <div className="mt-3 text-xs text-center truncate w-full">{d.name}</div>
                    <div className="text-[10px] font-mono text-stone mt-1">{humanSize(d.size)}</div>
                    {d.is_important && <Star size={12} className="text-accent mt-1" fill="currentColor" />}
                  </button>
                  <div className="border-t border-line px-2 py-1 flex justify-between">
                    <button onClick={() => downloadDoc(d)} className="p-1 text-stone hover:text-ink" aria-label="Download"><Download size={14} /></button>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger className="text-stone hover:text-ink p-1"><MoreHorizontal size={14} /></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem onClick={() => renameDoc(d)}>Rename</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleImportant(d)}>
                            {d.is_important ? "Unmark important" : "Mark important"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => deleteDoc(d)}>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border border-line bg-white">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-stone">
                <tr className="border-b border-line">
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Size</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Uploaded</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {subfolders.map((f) => (
                  <tr key={f.id} className="border-b border-line hover:bg-sand/40" data-testid={`row-folder-${f.id}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => openFolder(f)} className="inline-flex items-center gap-3">
                        <Folder size={16} className="text-ink/70" /> <span className="font-medium">{f.name}</span>
                      </button>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-stone">—</td>
                    <td className="px-4 py-3 hidden md:table-cell text-stone font-mono text-xs">{f.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger className="text-stone hover:text-ink p-1"><MoreHorizontal size={14} /></DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            <DropdownMenuItem onClick={() => renameFolder(f)}>Rename</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-destructive" onClick={() => deleteFolder(f)}>Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredDocs.map((d) => {
                  const Icon = iconForFile(d.content_type, d.name);
                  return (
                    <tr key={d.id} className="border-b border-line hover:bg-sand/40" data-testid={`row-doc-${d.id}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => downloadDoc(d)} className="inline-flex items-center gap-3">
                          <Icon size={16} className="text-ink/70" /> <span>{d.name}</span>
                          {d.is_important && <Star size={12} className="text-accent" fill="currentColor" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-stone">{humanSize(d.size)}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-stone font-mono text-xs">{d.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button onClick={() => downloadDoc(d)} className="text-stone hover:text-ink" aria-label="Download"><Download size={14} /></button>
                          {canManage && (
                            <DropdownMenu>
                              <DropdownMenuTrigger className="text-stone hover:text-ink p-1"><MoreHorizontal size={14} /></DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-white">
                                <DropdownMenuItem onClick={() => renameDoc(d)}>Rename</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => toggleImportant(d)}>
                                  {d.is_important ? "Unmark important" : "Mark important"}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-destructive" onClick={() => deleteDoc(d)}>Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
